import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { HeartHandshake, Eye, EyeOff, LogOut, Lock, ChevronRight, Plus } from "lucide-react";
import { myCommunities, updateCommunityVisibility, leaveCommunity } from "@/lib/api";
import { CommunityIcon, accentClasses } from "@/lib/communityIcons";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export const CommunitiesSection = ({ userRole }) => {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  const load = () => myCommunities().then(setItems);
  useEffect(() => { load(); }, []);

  const toggle = async (slug, val) => {
    await updateCommunityVisibility(slug, val);
    toast.success(val ? "Now visible on your resume" : "Hidden from your resume");
    load();
  };
  const leave = async (slug, name) => {
    await leaveCommunity(slug);
    toast.info(`You've left ${name}`);
    load();
  };

  const isEmployer = userRole === "employer";

  return (
    <div className="mt-6 rounded-lg border border-border bg-card p-6" data-testid="account-communities">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1.5">
            <HeartHandshake className="w-3.5 h-3.5" /> Communities & identities
          </div>
          <p className="text-sm text-foreground/80 mt-1">
            {isEmployer
              ? "Public pledges you've taken. Each pledge shows on your employer profile."
              : "Communities you've joined. Private by default — flip the switch to show on your public resume."}
          </p>
        </div>
        <Button data-testid="browse-communities-btn" variant="outline" size="sm" onClick={() => navigate("/communities")}>
          <Plus className="w-4 h-4 mr-1.5" /> Browse communities
        </Button>
      </div>

      {items === null ? (
        <div className="mt-4 text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground text-center">
          <Lock className="w-6 h-6 mx-auto opacity-50 mb-2" />
          You haven't joined any communities yet. Everything is opt-in and private by default.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((m) => {
            const c = m.community;
            const ac = accentClasses(c.accent);
            return (
              <div key={m.id} data-testid={`my-community-${c.slug}`} className="rounded-lg border border-border p-4 flex items-center gap-3">
                <button onClick={() => navigate(`/communities/${c.slug}`)}
                        className={`w-10 h-10 rounded-lg ${ac.bg} ${ac.text} flex items-center justify-center shrink-0`}>
                  <CommunityIcon name={c.icon} className="w-5 h-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <button onClick={() => navigate(`/communities/${c.slug}`)} className="font-heading font-bold text-left hover:text-primary transition-colors">
                    {c.name}
                  </button>
                  <div className="text-xs text-muted-foreground">
                    {m.role === "pledged_employer" ? "Pledged employer" : m.role === "ally" ? "Ally" : "Member"} · joined {new Date(m.joined_at).toLocaleDateString()}
                  </div>
                </div>
                {m.role !== "pledged_employer" && (
                  <label className="hidden sm:flex items-center gap-2 cursor-pointer text-xs text-muted-foreground">
                    {m.show_on_resume ? <Eye className="w-3.5 h-3.5 text-primary" /> : <EyeOff className="w-3.5 h-3.5" />}
                    Show on resume
                    <Switch data-testid={`visibility-${c.slug}`} checked={!!m.show_on_resume}
                            onCheckedChange={(v) => toggle(c.slug, v)} />
                  </label>
                )}
                <Button data-testid={`leave-${c.slug}`} variant="ghost" size="sm" onClick={() => leave(c.slug, c.name)}>
                  <LogOut className="w-4 h-4" />
                </Button>
                <ChevronRight className="w-4 h-4 text-muted-foreground hidden sm:block" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
